import { Form } from "antd";
import i18n from "i18next";
import { useCallback, useState } from "react";

import { formatValidationErrors } from "src/errorHandling";
import { useUpdateTableDataMutation } from "../../../../../components";
import type { ImportResult } from "../../../../../components/FeatureButtons/types";
import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { FORM_ACTION } from "../../../../../core/router/types/routerTypes";

interface UseMoveSiteModalLogicParams {
    messageKey?: string;
    errorMessageKey?: string;
    formatErrors?: boolean;
    isBatch?: boolean;
}

interface MoveSiteModalState {
    isModalVisible: boolean;
    uploading: boolean;
    importResult: ImportResult | null;
    showAllErrors: boolean;
    setIsModalVisible: (visible: boolean) => void;
    setUploading: (uploading: boolean) => void;
    setImportResult: (result: ImportResult | null) => void;
    setShowAllErrors: (show: boolean) => void;
}

interface MoveSiteModalCallbacks {
    showModal: () => void;
    onCancel: () => void;
    onOk: (bodyBuilder: () => Promise<Record<string, unknown>>) => Promise<void>;
}

export const useMoveSiteModalLogic = (
    form: ReturnType<typeof Form.useForm>[0],
    params?: UseMoveSiteModalLogicParams,
): [MoveSiteModalState, MoveSiteModalCallbacks] => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [showAllErrors, setShowAllErrors] = useState(false);

    const currentDataGroup = getAssignedDataGroup();
    const moveUrl = `${ResourceName.DATA_GROUPS}/${currentDataGroup.code}/${ResourceName.SITES}/${FORM_ACTION.MOVE}`;

    const [moveDataRequest] = useUpdateTableDataMutation();

    const showModal = useCallback(() => setIsModalVisible(true), []);

    const onCancel = useCallback(() => {
        form.resetFields();
        setIsModalVisible(false);
        setImportResult(null);
    }, [form]);

    const onOk = useCallback(
        async (bodyBuilder: () => Promise<Record<string, unknown>>) => {
            setUploading(true);

            try {
                const body = await bodyBuilder();

                moveDataRequest({
                    url: moveUrl,
                    method: "POST",
                    body: {
                        ...body,
                        ...(params?.isBatch !== undefined && { isBatch: params.isBatch }),
                    },
                })
                    .unwrap()
                    // @ts-ignore
                    .then((result) => setImportResult(result))
                    .catch((error) => {
                        const errorMessageKey = params?.errorMessageKey || "moveData.modal.importError";
                        let errorMessages: string[] = [];

                        if (params?.formatErrors) {
                            const errorMessage = formatValidationErrors(error?.data, i18n.t);
                            errorMessages = errorMessage ? errorMessage.split(/\r?\n/) : [];
                        }

                        if (errorMessages.length === 0) {
                            errorMessages = [error?.data?.message || errorMessageKey];
                        }

                        setImportResult({
                            importErrors: errorMessages,
                        });
                    })
                    .finally(() => setUploading(false));
            } catch (error) {
                setImportResult({
                    importErrors: [typeof error === "string" ? error : params?.errorMessageKey || "moveData.modal.importError"],
                });
                setUploading(false);
            }
        },
        [moveDataRequest, moveUrl, params],
    );

    return [
        {
            isModalVisible,
            uploading,
            importResult,
            showAllErrors,
            setIsModalVisible,
            setUploading,
            setImportResult,
            setShowAllErrors,
        },
        {
            showModal,
            onCancel,
            onOk,
        },
    ];
};
